import React, { useState } from 'react';
import { 
  FiSearch, FiRefreshCw, FiUserCheck, FiUsers, FiClock, 
  FiCheckCircle, FiPhone, FiMail, FiEye, FiX, FiAward 
} from 'react-icons/fi';
import { RiShareForwardLine } from 'react-icons/ri';

export default function ReferralsTab({ referrals = [], loading = false, onRefresh }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'verified' | 'pending'
  const [selectedReferral, setSelectedReferral] = useState(null);

  const colors = {
    blue: '#2f3194',
    orange: '#f35018',
    cyan: '#63decc',
    textMain: '#0f1137',
    muted: '#64748b',
    border: '#e2e8f0',
    white: '#ffffff',
    success: '#10b981',
    danger: '#ef4444',
  };

  const filteredReferrals = referrals.filter((r) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = 
      (r.referrer_name && r.referrer_name.toLowerCase().includes(q)) ||
      (r.referrer_mobile && r.referrer_mobile.includes(q)) ||
      (r.new_user_name && r.new_user_name.toLowerCase().includes(q)) ||
      (r.new_user_mobile && r.new_user_mobile.includes(q)) ||
      (r.new_user_email && r.new_user_email.toLowerCase().includes(q));

    const matchesStatus = statusFilter === 'all' ? true : r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalReferrals = referrals.length;
  const verifiedCount = referrals.filter(r => r.status === 'verified').length;
  const pendingCount = totalReferrals - verifiedCount;
  const approvedUsersFromRef = referrals.filter(r => r.new_user_is_approved === 1).length;

  return (
    <div>
      {/* KPI STATS CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={cardStyle}>
          <div style={iconBox('rgba(47, 49, 148, 0.1)', colors.blue)}>
            <RiShareForwardLine size={22} color={colors.blue} />
          </div>
          <div>
            <p style={kpiLabel}>TOTAL REFERRALS LOGGED</p>
            <h3 style={kpiVal}>{totalReferrals}</h3>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={iconBox('rgba(16, 185, 129, 0.1)', colors.success)}>
            <FiCheckCircle size={22} color={colors.success} />
          </div>
          <div>
            <p style={kpiLabel}>WHATSAPP OTP VERIFIED</p>
            <h3 style={{ ...kpiVal, color: colors.success }}>{verifiedCount}</h3>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={iconBox('rgba(243, 80, 24, 0.1)', colors.orange)}>
            <FiClock size={22} color={colors.orange} />
          </div>
          <div>
            <p style={kpiLabel}>PENDING VERIFICATION</p>
            <h3 style={{ ...kpiVal, color: colors.orange }}>{pendingCount}</h3>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={iconBox('rgba(99, 222, 204, 0.15)', '#0f766e')}>
            <FiUserCheck size={22} color="#0f766e" />
          </div>
          <div>
            <p style={kpiLabel}>REFEREES APPROVED</p>
            <h3 style={{ ...kpiVal, color: '#0f766e' }}>{approvedUsersFromRef}</h3>
          </div>
        </div>
      </div>

      {/* TABLE CONTROLS CARD */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', border: `1px solid ${colors.border}`, padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: colors.textMain }}>
              Referred Signups Directory
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: colors.muted }}>
              Track members invited by approved hosts via verified WhatsApp OTP invitations.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Status Filter Tabs */}
            <div style={{ display: 'flex', backgroundColor: '#f1f5f9', borderRadius: '10px', padding: '3px' }}>
              {['all', 'verified', 'pending'].map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setStatusFilter(st)}
                  style={{
                    border: 'none',
                    padding: '6px 14px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '700',
                    textTransform: 'capitalize',
                    cursor: 'pointer',
                    backgroundColor: statusFilter === st ? colors.white : 'transparent',
                    color: statusFilter === st ? colors.blue : colors.muted,
                    boxShadow: statusFilter === st ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  {st}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#f8fafc', border: `1px solid ${colors.border}`, borderRadius: '10px', padding: '6px 12px' }}>
              <FiSearch size={15} color={colors.muted} style={{ marginRight: '6px' }} />
              <input
                type="text"
                placeholder="Search referrer, referee, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '12px', width: '210px' }}
              />
            </div>

            <button onClick={onRefresh} style={iconBtnStyle} title="Refresh Table">
              <FiRefreshCw size={15} color={colors.blue} className={loading ? 'spin' : ''} />
            </button>
          </div>
        </div>

        {/* DATA TABLE */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc', borderBottom: `1px solid ${colors.border}` }}>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>REFERRER (EXISTING HOST)</th>
                <th style={thStyle}>NEW REGISTERED USER (REFEREE)</th>
                <th style={thStyle}>DATE INVITED</th>
                <th style={thStyle}>WHATSAPP OTP</th>
                <th style={thStyle}>REFEREE STATUS</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>DETAILS</th>
              </tr>
            </thead>
            <tbody>
              {filteredReferrals.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: colors.muted }}>
                    No referral records matched your search criteria.
                  </td>
                </tr>
              ) : (
                filteredReferrals.map((r) => {
                  const isVerified = r.status === 'verified';
                  const isRefereeApproved = r.new_user_is_approved === 1;

                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '14px 10px', fontWeight: '800', color: colors.blue }}>#{r.id}</td>

                      {/* Referrer Details */}
                      <td style={{ padding: '14px 10px' }}>
                        <strong style={{ color: colors.textMain, display: 'block' }}>
                          {r.referrer_name || 'Approved Host'}
                        </strong>
                        <span style={{ fontSize: '11px', color: colors.muted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <FiPhone size={10} /> {r.referrer_mobile}
                        </span>
                      </td>

                      {/* Referee Details */}
                      <td style={{ padding: '14px 10px' }}>
                        <strong style={{ color: colors.textMain, display: 'block' }}>
                          {r.new_user_name || 'Pending Onboarding'}
                        </strong>
                        <span style={{ fontSize: '11px', color: colors.muted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <FiMail size={10} /> {r.new_user_email}
                        </span>
                        <span style={{ fontSize: '11px', color: colors.muted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <FiPhone size={10} /> {r.new_user_mobile}
                        </span>
                      </td>

                      <td style={{ padding: '14px 10px', color: colors.muted }}>
                        {new Date(r.created_at).toLocaleDateString()}
                      </td>

                      {/* OTP Status */}
                      <td style={{ padding: '14px 10px' }}>
                        <span style={{
                          padding: '3px 10px',
                          borderRadius: '20px',
                          fontSize: '11px',
                          fontWeight: '800',
                          backgroundColor: isVerified ? '#ecfdf5' : '#fff1f2',
                          color: isVerified ? colors.success : colors.danger,
                          border: `1px solid ${isVerified ? '#a7f3d0' : '#fecdd3'}`
                        }}>
                          {isVerified ? '✓ OTP Verified' : '✕ Pending OTP'}
                        </span>
                      </td>

                      {/* Referee Approval */}
                      <td style={{ padding: '14px 10px' }}>
                        <span style={{
                          padding: '3px 10px',
                          borderRadius: '20px',
                          fontSize: '11px',
                          fontWeight: '800',
                          backgroundColor: isRefereeApproved ? '#eff6ff' : '#fff7ed',
                          color: isRefereeApproved ? colors.blue : colors.orange
                        }}>
                          {isRefereeApproved ? 'Host Approved' : 'Approval Pending'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '14px 10px', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setSelectedReferral(r)}
                          style={{
                            border: 'none',
                            background: 'rgba(47, 49, 148, 0.08)',
                            color: colors.blue,
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontWeight: '700',
                            fontSize: '11px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <FiEye size={12} /> View Details
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* REFERRAL DETAILS MODAL */}
      {selectedReferral && (
        <div style={modalBackdrop} onClick={() => setSelectedReferral(null)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: `1px solid ${colors.border}` }}>
              <div>
                <span style={{ fontSize: '11px', fontWeight: '800', color: colors.orange, textTransform: 'uppercase' }}>
                  Referral Audit Record
                </span>
                <h4 style={{ margin: 0, color: colors.blue, fontWeight: '800' }}>
                  Referral #{selectedReferral.id}
                </h4>
              </div>
              <button onClick={() => setSelectedReferral(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: colors.muted }}>
                <FiX size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              {/* Referrer Box */}
              <div style={{ padding: '14px', borderRadius: '12px', backgroundColor: '#f8fafc', border: `1px solid ${colors.border}` }}>
                <span style={{ fontSize: '11px', fontWeight: '800', color: colors.blue, textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  Invited By (Referrer)
                </span>
                <strong style={{ fontSize: '14px', color: colors.textMain, display: 'block' }}>
                  {selectedReferral.referrer_name || 'N/A'}
                </strong>
                <span style={{ fontSize: '12px', color: colors.muted, display: 'block', marginTop: '4px' }}>
                  Mobile: <strong>{selectedReferral.referrer_mobile}</strong>
                </span>
                <span style={{ fontSize: '12px', color: colors.muted, display: 'block' }}>
                  Email: {selectedReferral.referrer_email || 'N/A'}
                </span>
                <div style={{ marginTop: '8px' }}>
                  <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', backgroundColor: '#e2e8f0', fontWeight: '700', color: colors.textMain }}>
                    Plan: {(selectedReferral.referrer_plan_tier || 'trial').toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Referee Box */}
              <div style={{ padding: '14px', borderRadius: '12px', backgroundColor: '#f8fafc', border: `1px solid ${colors.border}` }}>
                <span style={{ fontSize: '11px', fontWeight: '800', color: colors.orange, textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  New Member (Referee)
                </span>
                <strong style={{ fontSize: '14px', color: colors.textMain, display: 'block' }}>
                  {selectedReferral.new_user_name || 'Pending Registration'}
                </strong>
                <span style={{ fontSize: '12px', color: colors.muted, display: 'block', marginTop: '4px' }}>
                  Mobile: <strong>{selectedReferral.new_user_mobile}</strong>
                </span>
                <span style={{ fontSize: '12px', color: colors.muted, display: 'block' }}>
                  Email: {selectedReferral.new_user_email}
                </span>
                <div style={{ marginTop: '8px', display: 'flex', gap: '6px' }}>
                  <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', backgroundColor: selectedReferral.new_user_is_approved ? '#ecfdf5' : '#fff7ed', color: selectedReferral.new_user_is_approved ? colors.success : colors.orange, fontWeight: '700' }}>
                    {selectedReferral.new_user_is_approved ? 'Approved Host' : 'Pending Approval'}
                  </span>
                </div>
              </div>
            </div>

            {/* Verification Metadata */}
            <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#f1f5f9', fontSize: '12px', color: colors.muted, display: 'flex', justifyContent: 'space-between' }}>
              <span>Verification Method: <strong style={{ color: colors.textMain }}>WhatsApp OTP Gateway</strong></span>
              <span>Logged At: <strong style={{ color: colors.textMain }}>{new Date(selectedReferral.created_at).toLocaleString()}</strong></span>
            </div>

            <button
              onClick={() => setSelectedReferral(null)}
              style={{ marginTop: '16px', width: '100%', padding: '10px 0', border: 'none', borderRadius: '10px', backgroundColor: colors.blue, color: colors.white, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}
            >
              Close Record
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const cardStyle = {
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  padding: '18px 20px',
  border: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
};
const iconBox = (bg, color) => ({
  width: '46px',
  height: '46px',
  borderRadius: '12px',
  backgroundColor: bg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
const kpiLabel = { fontSize: '11px', fontWeight: '800', color: '#64748b', margin: 0, letterSpacing: '0.5px' };
const kpiVal = { fontSize: '24px', fontWeight: '900', color: '#0f1137', margin: '2px 0 0 0' };
const thStyle = { padding: '12px 10px', textAlign: 'left', fontWeight: '700', fontSize: '11px', color: '#64748b', letterSpacing: '0.4px' };
const iconBtnStyle = { width: '36px', height: '36px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const modalBackdrop = { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 17, 55, 0.7)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' };
const modalCard = { backgroundColor: '#ffffff', borderRadius: '20px', padding: '28px', maxWidth: '580px', width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' };